import React from 'react';
import { useLocation } from 'react-router-dom';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import config from '@plone/volto/registry';

import useSiteConfig from '../../../../helpers/useSiteConfig';
import useSearchControls from '../../../../helpers/useSearchControls';
import useCanonicalSearchUrl from '../../../../helpers/useCanonicalSearchUrl';
import PageHeader from '../../../../components/Layout/PageHeader';
import ResultsPager from '../../../../components/Layout/ResultsPager';
import { pageFrom, pageKeyFor } from '../../../../components/Browse/browseState';
import { getSearchAppData } from '../../../../config/searchApp';

/**
 * The Search App — SHADOW of @plone/volto theme/Search/Search (the component the
 * `/search` route renders).
 *
 * Stock Volto's /search is a bespoke page bound to the `@search` endpoint
 * (plain catalog). We replace it with the project's branded, config-driven
 * search block, which dispatches to `@querystring-search` — the backend Solr
 * swap-seam — so /search gets the full Solr experience (ranking, recall,
 * inside-PDF text) with zero bespoke result-rendering code: the block reuses the
 * same SearchInput / Facets / SortOn / TableListing chrome as the browse pages.
 *
 * The block is posted at the SITE ROOT (`path="/"`): the Solr override is
 * registered for the site root (IPloneSiteRoot); sub-container calls fall to the
 * stock IContentish service. getBaseUrl('/') -> '' resolves to the root.
 *
 * All search behaviour (types, facets, columns) is data — see
 * src/config/searchApp.ts. This wrapper only supplies the page frame, which is
 * now the SHARED one: `.pp-page` + `<PageHeader>` + `<ResultsPager>` are the
 * same components the browse containers mount (item 15, theme/page.less). The
 * page's own furniture — the search box, the status/match strip — lives inside
 * the block, where the query it modifies is.
 */
const SEARCH_BLOCK_ID = 'search-app';

const SearchApp = () => {
  const site = useSiteConfig();
  const location = useLocation();
  const SearchView = config.blocks.blocksConfig.search.view;
  // Two things core cannot do on a route with no content object behind it:
  // consume the inbound `?SearchableText=` seed once the block owns the query
  // (without this, the masthead box's own URL makes the search unchangeable),
  // and reset the page when the result set changes. See the helper.
  useCanonicalSearchUrl();
  // The status/operator controls live in the URL (see helpers/useSearchControls),
  // so the block data is a function of the URL. Memoized on the two values that
  // matter — the block's deep-compare effects re-fire on any new object, and a
  // re-fire here is a re-query.
  const [controls] = useSearchControls();
  const data = React.useMemo(
    () =>
      getSearchAppData({
        statusGroup: controls.statusGroup,
        operator: controls.operator,
        correct: controls.correct,
      }),
    [controls.statusGroup, controls.operator, controls.correct],
  );

  // This route has no content object, so it has no other paginated block: the
  // key is the bare `page`, which is what core's usePagination derives here too
  // (same rule, one implementation — see Browse/browseState.pageKeyFor).
  const pageKey = pageKeyFor(null, SEARCH_BLOCK_ID);
  const currentPage = pageFrom(location?.search, pageKey);

  // The indexable-surface split. (item 11)
  //
  // /search is a TOOL, not a page: its content is whatever the last visitor
  // typed, so indexing it produces a listing that matches a query nobody asked.
  // It is nonetheless worth CRAWLING — it links to real documents, and its
  // no-term state is a genuine 50-row list of current material — hence
  // noindex,follow rather than a robots.txt block. robots.txt does the
  // complementary half: it blocks `/search?`, the unbounded parameter space,
  // while leaving this page fetchable so the directive below is actually seen.
  //
  // The canonical points at the bare /search for every parameterisation, so
  // any variant that does get discovered consolidates onto one URL instead of
  // competing with itself.
  //
  // The indexable half of the archive is the BROWSE layer: /documents?preset=…
  // and friends are curated, stable, and marked index,follow by BrowseSEO.
  const searchUrl = `${(site.baseUrl || '').replace(/\/+$/, '')}/search`;

  return (
    <div id="view" className="pp-page search-app">
      <Helmet>
        <title>{`Search — ${site.title}`}</title>
        <meta name="robots" content="noindex,follow" />
        <link rel="canonical" href={searchUrl} />
        {/* BL-0157 (TAIUIT seo/twitter-card, og:title): the tool page carried
            og:url only, so a shared link had no title or card type. */}
        <meta property="og:title" content={`Search — ${site.title}`} />
        <meta name="twitter:card" content="summary" />
        <meta property="og:url" content={searchUrl} />
        <meta property="og:site_name" content={site.title} />
        <meta
          name="description"
          content={`Full-text search across every document in ${site.title}, matching titles, document numbers, and the text inside the PDFs.`}
        />
      </Helmet>
      <PageHeader
        title={`Search ${site.title}`}
        lede="Full-text search across every document — matching titles, document numbers, and the text inside the PDFs."
      />
      <SearchView id={SEARCH_BLOCK_ID} data={data} path="/" mode="view" />
      <ResultsPager
        id={SEARCH_BLOCK_ID}
        pathname={location?.pathname || '/search'}
        search={location?.search}
        pageKey={pageKey}
        currentPage={currentPage}
        bSize={data?.query?.b_size}
      />
    </div>
  );
};

export default SearchApp;
